/** @format */

import { FetchBaseQueryError, skipToken } from "@reduxjs/toolkit/query";
import React, { useState } from "react";
import { Buffer } from "buffer";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import FeatureFlag from "../../featureFlag/FeatureFlag";
import { useGetUserQuery } from "../../../app/services/userApi";
import { ResourceReference } from "../../../types";
import { resourceApiSlice } from "../../../app/services/logsApi";
import UserAvatar from "../../user/UserAvatar";
import { store } from "../../../app/store";
import LoadingToast from "../../notifications/LoadingToast";
import { setAddNotifications } from "../../notifications/notificationsSlice";

type Props = {
  resource: ResourceReference;
};

const ResourceLogs: React.FC<Props> = ({ resource }) => {
  const { t: _ } = useTranslation();

  const [isLogsDownloading, setIsLogsDownloading] = useState<boolean>(false);

  const isAuthenticated =
    useAppSelector((state) => state.auth.userToken?.token) || false;
  const { data: user } = useGetUserQuery(
    !!isAuthenticated ? undefined : skipToken,
  );

  const isStaff = user?.role === "wjec" || user?.role === "admin";

  /**
   * save log data from both resource and log item to JSON file
   */
  const exportData = async () => {
    setIsLogsDownloading(true);
    const { data: logs, error } = await store.dispatch(
      resourceApiSlice.endpoints.getResourceLogs.initiate({
        itemPK_B64: Buffer.from(resource.PK).toString("base64"),
        itemSK_B64: Buffer.from(resource.SK).toString("base64"),
      }),
    );

    if (
      error === undefined ||
      // api returns 404 if no saved log, in this case only use lastLog but don't cancel export
      (error as FetchBaseQueryError).status !== 404 ||
      resource.lastLog === undefined
    ) {
      store.dispatch(
        setAddNotifications({
          date: new Date().toISOString(),
          id: `resource-${resource.Code}-logs`,
          title: _("downloadLogs.title"),
          msg: `${resource.Code}`,
          type: "danger",
        }),
      );
      setIsLogsDownloading(false);
      return;
    }

    const fullLogs = [...(logs?.logs ?? []), resource.lastLog];
    const jsonString = `data:text/json;chatset=utf-8,${encodeURIComponent(
      JSON.stringify(fullLogs, null, 2),
    )}`;
    const link = document.createElement("a");
    link.href = jsonString;
    link.download = `${resource.Code}-logs.json`;
    link.click();
    link.remove();

    setIsLogsDownloading(false);
  };

  return (
    <FeatureFlag flag={["dev", "logs"]}>
      <div
        className="flex-shrink-0 text-center"
        onClick={isStaff ? exportData : () => {}}
        title={isStaff ? _("downloadLogs.title") : ""}
        role={isStaff ? "button" : ""}
        data-testid="logs"
      >
        {isStaff && (
          <UserAvatar
            logUser={resource.lastLog?.user}
            title={resource.lastLog?.user.displayName}
          />
        )}

        <small className="ms-3">
          <span className="fst-italic pe-auto">{_("Last Updated")}:</span>
          &nbsp;
          {new Date(
            resource.lastLog?.time ?? resource.lastEdit?.time ?? "",
          ).toLocaleDateString()}
        </small>
      </div>
      <LoadingToast isLoading={isLogsDownloading} />
    </FeatureFlag>
  );
};

export default ResourceLogs;
